import {
  isStorageSupportSpace,
  isWorkCategory,
} from 'spw-components/worksheet/utils';
import {
  AMENITY_SPACES_CATEGORY,
  CUSTOM_SUBCATEGORY,
  GROWTH_CALCULATORS,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import {
  ICategoryNote,
  IWorksheet,
  IWorksheetRow,
  WorksheetCategory,
} from 'spw-redux/modules/calculators/types';
import { IProjectSettings } from 'spw-redux/modules/settings/types';
import { Category, Subcategory } from 'spw-redux/modules/space_types/types';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import { PlainProgramScenario } from './Scenario';
import WorksheetRow from './WorksheetRow';
import WorksheetStorageSpaceRow from './WorksheetStorageSpaceRow';

type ScenarioConfig = {
  isChildBasedCalculation: boolean;
  isSharedConfigDetailed: boolean;
};

export type PlainWorksheet = Omit<
  Worksheet,
  | 'calculateTotals'
  | 'calculateSharedSpacesUSF'
  | 'haveSharedSpacesAssignedSpace'
  | 'increaseCategoryTotals'
  | 'increaseSubcategoryTotals'
  | 'increaseSpaceTypesTotals'
  | 'shouldExcludeRowForTotals'
  | 'calculateSubTotalProgramUSF'
  | 'toPlainObject'
>;

export type Totals = {
  USF: number;
  seats: number;
  spacesQuantity: number;
  seatMix?: number;
  ratio?: number;
  lockersOrDrawersQuantity?: number;
  dividendForRatio?: number;
};

export default class Worksheet {
  scenario: PlainProgramScenario;

  readonly projectSettings: IProjectSettings;

  subtotalProgramUSF = 0;

  workspacesSeats = 0;

  workspacesUSF = 0;

  allCategoriesSeats = 0;

  rows: WorksheetRow[];

  sharedSpacesAssignedSpacePercentage = 0;

  sharedSpacesUSF?: number;

  categoriesTotals: Record<string, Totals> = {};

  subcategoriesTotals: Record<string, Totals> = {};

  spaceTypesTotals: Record<string, Totals> = {};

  hasAtLeastOneRowWithQuantity = false;

  rowsByCategory: Record<Category | string, WorksheetRow[]> = {
    individualWorkspaces: [],
    meetingSpaces: [],
    amenitySpaces: [],
    supportSpaces: [],
    customCategory: [],
  };

  rowsBySubcategory: Record<
    Subcategory | string,
    WorksheetRow[]
  > = {} as Record<Subcategory | string, WorksheetRow[]>;

  breakDownRows: Record<string, WorksheetRow[]> = {};

  hasWorkspacesNotIncludedInTotalSeats?: boolean;

  hasStorageRows = false;

  hasNestedRows = false;

  id: number;

  notes: ICategoryNote[];

  categories: WorksheetCategory[];

  constructor(
    apiWorksheet: IWorksheet,
    scenario: PlainProgramScenario,
    projectSettings: IProjectSettings
  ) {
    this.scenario = scenario;
    this.projectSettings = projectSettings;
    this.rows = [];
    this.id = apiWorksheet?.id;
    this.notes = apiWorksheet.notes;
    this.categories = apiWorksheet.categories;

    if (scenario.calculatorType === GROWTH_CALCULATORS.macroProgramming)
      this.sharedSpacesAssignedSpacePercentage = apiWorksheet.spaceAllocation;

    this.calculateTotals(apiWorksheet.rows, projectSettings);
  }

  calculateTotals = (
    rows: IWorksheetRow[],
    projectSettings: IProjectSettings
  ) => {
    if (!rows) return;
    this.rows = [];
    rows.forEach((r) => {
      let row = this.createRow(r, projectSettings);
      // Needed to ignore rows that were copied over from a worksheet with a different
      // individual and shared spaces config
      if (this.shouldRowBeIgnored(row)) return null;
      const shouldExcludeRowForTotals = this.shouldExcludeRowForTotals(row);
      if (shouldExcludeRowForTotals) row = WorksheetRow.removeRowValues(row);
      this.placeIntoCorrespondingCategory(row);
      this.setWorksheetHasStorageRows(row);
      this.setWorksheetHasNestedRows(row);
      if (Number.isNaN(row.quantity)) return this.rows.push(row);
      if (shouldExcludeRowForTotals) {
        // Check if the parent rows of custom subcategory rows are not included in total seats
        this.setFlagForWorkspacesNotIncludedInTotalSeats(row);
        return this.rows.push(row);
      }
      this.increaseCategoryTotals(row);
      if (row.subcategory) this.increaseSubcategoryTotals(row);
      this.allCategoriesSeats += row.seatCap * row.quantity;
      if (row.quantity > 0) this.hasAtLeastOneRowWithQuantity = true;

      this.increaseSpaceTypesTotals(row);
      this.setFlagForWorkspacesNotIncludedInTotalSeats(row);
      return this.rows.push(row);
    });

    Object.keys(this.spaceTypesTotals).forEach((spaceTypeName: string) => {
      const quantity =
        this.spaceTypesTotals[spaceTypeName].lockersOrDrawersQuantity ||
        this.spaceTypesTotals[spaceTypeName].spacesQuantity;
      const dividend =
        this.spaceTypesTotals[spaceTypeName].dividendForRatio ||
        this.scenario.workSeats;
      this.spaceTypesTotals[spaceTypeName].ratio = dividend / quantity;
    });
    this.workspacesSeats =
      this.categoriesTotals.individualWorkspaces?.seats || 0;
    this.workspacesUSF = this.categoriesTotals.individualWorkspaces?.USF || 0;

    this.subtotalProgramUSF = this.calculateSubTotalProgramUSF();

    this.sharedSpacesUSF = this.calculateSharedSpacesUSF();
    if (this.haveSharedSpacesAssignedSpace()) {
      this.subtotalProgramUSF += this.sharedSpacesUSF;
    }
  };

  private setFlagForWorkspacesNotIncludedInTotalSeats = (row: WorksheetRow) => {
    if (!this.hasWorkspacesNotIncludedInTotalSeats)
      this.hasWorkspacesNotIncludedInTotalSeats =
        !row.removed &&
        row.subcategory === CUSTOM_SUBCATEGORY &&
        !row.projectSpaceTypeSubcategory.includeInTotalWorkSeats;
  };

  private setWorksheetHasStorageRows = (row: WorksheetRow) => {
    if (
      !row.removed &&
      row.isStorageSupportSpace &&
      (!Number.isNaN(row.quantity) || !row.child)
    )
      this.hasStorageRows = true;
  };

  private setWorksheetHasNestedRows = (row: WorksheetRow) => {
    if (!row.removed && row.child) this.hasNestedRows = true;
  };

  private shouldRowBeIgnored = (row: WorksheetRow) => {
    const isSummaryScenario = !this.scenario.isDetailed;
    const isChildWorkspaceRow = row.child && !row.isStorageSupportSpace;

    if (isChildWorkspaceRow && isSummaryScenario) return true;
    const doesRowBelongToSharedSpaces = !isIWCategoryOrSubcategory(
      row.projectSpaceTypeCategory
    );

    const isMacroSharedScenario = this.scenario.isShared;
    if (doesRowBelongToSharedSpaces && isMacroSharedScenario) return true;
    return false;
  };

  shouldExcludeRowForTotals = (row: WorksheetRow) => {
    if (row.removed) return true;
    const isDetailedScenario = this.scenario.isDetailed;
    return (
      isWorkCategory(row) &&
      ((!isDetailedScenario && row.child) || (isDetailedScenario && !row.child))
    );
  };

  calculateSubTotalProgramUSF = () => {
    return Object.keys(this.categoriesTotals).reduce(
      (total, key) => total + this.categoriesTotals[key].USF,
      0
    );
  };

  placeIntoCorrespondingCategory = (row: WorksheetRow) => {
    if (row.removed) return;
    if (this.rowsByCategory[row.category as Category])
      this.rowsByCategory[row.category as Category].push(row);
    else this.rowsByCategory[row.category] = [row];

    if (row.subcategory) {
      if (this.rowsBySubcategory[row.subcategory])
        this.rowsBySubcategory[row.subcategory].push(row);
      else this.rowsBySubcategory[row.subcategory] = [row];
    }
  };

  increaseCategoryTotals = (row: WorksheetRow) => {
    const previousTotals = this.categoriesTotals[row.category] || {
      USF: 0,
      seats: 0,
      spacesQuantity: 0,
      seatMix: 0,
    };

    const shouldExcludeRowFromTotalQuantity = !!row.isStorageSupportSpace;
    const newQuantity = shouldExcludeRowFromTotalQuantity
      ? previousTotals.spacesQuantity
      : previousTotals.spacesQuantity + row.quantity;
    this.categoriesTotals[row.category] = {
      USF: previousTotals.USF + row.usf * row.quantity,
      seats:
        previousTotals.seats +
        (row.isIncludedInTotalWorkSeats ? row.seatCap * row.quantity : 0),
      spacesQuantity: newQuantity,
    };
  };

  increaseSubcategoryTotals = (row: WorksheetRow) => {
    const previousTotals = this.subcategoriesTotals[row.subcategory] || {
      USF: 0,
      seats: 0,
      spacesQuantity: 0,
    };

    this.subcategoriesTotals[row.subcategory] = {
      USF: previousTotals.USF + row.usf * row.quantity,
      seats: previousTotals.seats + row.seatCap * row.quantity,
      spacesQuantity: previousTotals.spacesQuantity + row.quantity,
    };
  };

  increaseSpaceTypesTotals = (row: WorksheetRow) => {
    if (row.removed) return;
    if (!this.scenario.isDetailed && !row.isStorageSupportSpace) {
      if (!row.child)
        this.spaceTypesTotals[row.uniqueKey] = {
          USF: row.usf * row.quantity,
          seats: row.seatCap * row.quantity,
          spacesQuantity: row.quantity,
          seatMix: row.ratio,
        };

      return;
    }
    if (!row.spaceType.parent) return;
    const parentSpaceTypeKey = `${row.spaceType.parent.name}_${row.spaceType.parent.id}`;

    const previousTotals = this.spaceTypesTotals[parentSpaceTypeKey] || {
      USF: 0,
      seats: 0,
      spacesQuantity: 0,
      seatMix: 0,
      lockersOrDrawersQuantity: 0,
    };

    this.spaceTypesTotals[parentSpaceTypeKey] = {
      USF: previousTotals.USF + row.usf * row.quantity,
      seats: previousTotals.seats + row.seatCap * row.quantity,
      spacesQuantity: previousTotals.spacesQuantity + row.quantity,
      seatMix: previousTotals.seatMix + row.ratio,
      lockersOrDrawersQuantity:
        previousTotals.lockersOrDrawersQuantity +
          (row as WorksheetStorageSpaceRow).lockersOrDrawersQuantity || 0,
      dividendForRatio: (row as WorksheetStorageSpaceRow).dividendForRatio,
    };
  };

  calculateSharedSpacesUSF = () => {
    if (!this.scenario.isShared) {
      const amenityUSF =
        this.categoriesTotals[AMENITY_SPACES_CATEGORY]?.USF || 0;
      const meetingUSF =
        this.categoriesTotals[MEETING_SPACES_CATEGORY]?.USF || 0;
      const supportUSF =
        this.categoriesTotals[SUPPORT_SPACES_CATEGORY]?.USF || 0;
      return amenityUSF + meetingUSF + supportUSF;
    }
    return (
      (this.subtotalProgramUSF * this.sharedSpacesAssignedSpacePercentage) /
      (100 - this.sharedSpacesAssignedSpacePercentage)
    );
  };

  haveSharedSpacesAssignedSpace = () =>
    this.sharedSpacesAssignedSpacePercentage !== undefined &&
    this.sharedSpacesAssignedSpacePercentage !== 0;

  toPlainObject = (): PlainWorksheet => JSON.parse(JSON.stringify(this));

  private createRow = (
    row: IWorksheetRow,
    projectSettings: IProjectSettings
  ): WorksheetRow => {
    if (isStorageSupportSpace(row.spaceType))
      return new WorksheetStorageSpaceRow(
        row,
        this.scenario,
        projectSettings,
        this.id
      );
    return new WorksheetRow(row, this.id);
  };

  static recalculate = (
    ws: PlainWorksheet,
    newRows: WorksheetRow[],
    excludeDeletedRows = false
  ) => {
    return new Worksheet(
      Worksheet.toAPIWorksheet({ ...ws, rows: newRows }, excludeDeletedRows),
      ws.scenario,
      ws.projectSettings
    ).toPlainObject();
  };

  static toAPIWorksheet = (
    ws: Worksheet | PlainWorksheet,
    excludeDeletedRows = false
  ): IWorksheet => {
    const rows: IWorksheetRow[] = [];
    ws.rows.forEach((r) => {
      if (r.removed && excludeDeletedRows) return;
      const row = r.isStorageSupportSpace
        ? WorksheetStorageSpaceRow.toWorksheetRow(r, ws.scenario.workSeats)
        : r;
      rows.push(WorksheetRow.toAPIWorksheetRow(row));
    });
    return {
      scenarioId: ws.scenario.id,
      id: ws.id,
      rows,
      spaceAllocation: ws.sharedSpacesAssignedSpacePercentage,
      notes: ws.notes,
      categories: ws.categories,
    };
  };
}

export { Totals as WorksheetTotals };
